import React, { useState, useEffect } from 'react';
import { 
    StarIcon,
    ChartBarIcon,
    ArrowTrendingUpIcon,
    ArrowTrendingDownIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { reviewsApi } from '../../utils/api';

/**
 * Composant pour afficher les statistiques des avis d'un produit
 * Inclut la note moyenne, la répartition des étoiles et les tendances
 */
const ReviewStats = ({ productId, onStatsLoad }) => {
    // États pour les statistiques
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Charger les statistiques
    const fetchStats = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = (await reviewsApi.getReviewStats(productId)).data;
            console.log('response', response);
            if (response.data) {
                setStats(response.data);
                
                // Notifier le composant parent
                if (onStatsLoad) {
                    onStatsLoad(response.data);
                }
            }
        } catch (err) {
            console.error('Erreur chargement stats avis:', err);
            setError(err.message || 'Erreur lors du chargement des statistiques');
        } finally {
            setLoading(false);
        }
    };

    // Effet pour charger les stats au montage
    useEffect(() => {
        if (productId) {
            fetchStats();
        }
    }, [productId]);

    // Rendu des étoiles avec note
    const renderStarsWithRating = (rating, size = "h-5 w-5") => {
        const fullStars = Math.floor(rating);
        const hasHalfStar = rating % 1 >= 0.5;
        
        return (
            <div className="flex items-center">
                <div className="flex">
                    {[1, 2, 3, 4, 5].map((star) => (
                        <div key={star} className="relative">
                            {star <= fullStars ? (
                                <StarIconSolid className={`${size} text-yellow-400`} />
                            ) : star === fullStars + 1 && hasHalfStar ? (
                                <div className="relative">
                                    <StarIcon className={`${size} text-gray-300`} />
                                    <div className="absolute inset-0 overflow-hidden w-1/2">
                                        <StarIconSolid className={`${size} text-yellow-400`} />
                                    </div>
                                </div>
                            ) : (
                                <StarIcon className={`${size} text-gray-300`} />
                            )}
                        </div>
                    ))}
                </div>
                <span className="ml-2 text-sm font-medium text-gray-700">
                    {parseFloat(rating).toFixed(1)}
                </span>
            </div>
        );
    };

    // Calculer les pourcentages pour les barres de progression
    const calculatePercentages = () => {
        if (!stats || stats.total_reviews === 0) return {};
        
        return {
            rating_5: Math.round((stats.rating_5 / stats.total_reviews) * 100),
            rating_4: Math.round((stats.rating_4 / stats.total_reviews) * 100),
            rating_3: Math.round((stats.rating_3 / stats.total_reviews) * 100),
            rating_2: Math.round((stats.rating_2 / stats.total_reviews) * 100),
            rating_1: Math.round((stats.rating_1 / stats.total_reviews) * 100)
        };
    };

    // Déterminer la tendance des avis
    const getTrendInfo = () => {
        if (!stats) return null;
        
        const totalReviews = stats.total_reviews;
        const positiveReviews = stats.rating_4 + stats.rating_5;
        const negativeReviews = stats.rating_1 + stats.rating_2;
        
        const positivePercentage = totalReviews > 0 ? (positiveReviews / totalReviews) * 100 : 0;
        const negativePercentage = totalReviews > 0 ? (negativeReviews / totalReviews) * 100 : 0;
        
        let trend = 'neutral';
        let message = 'Avis équilibrés';
        
        if (positivePercentage >= 70) {
            trend = 'positive';
            message = 'Très bien noté';
        } else if (positivePercentage >= 50) {
            trend = 'positive';
            message = 'Bien noté';
        } else if (negativePercentage >= 50) {
            trend = 'negative';
            message = 'Avis mitigés';
        }
        
        return { trend, message, positivePercentage, negativePercentage };
    };

    if (loading) {
        return (
            <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="animate-pulse">
                    <div className="flex items-center space-x-4 mb-4">
                        <div className="h-6 bg-gray-300 rounded w-24"></div>
                        <div className="h-4 bg-gray-300 rounded w-16"></div>
                    </div>
                    <div className="space-y-2">
                        {[...Array(5)].map((_, i) => (
                            <div key={i} className="flex items-center space-x-2">
                                <div className="h-3 bg-gray-300 rounded w-4"></div>
                                <div className="flex-1 h-2 bg-gray-300 rounded"></div>
                                <div className="h-3 bg-gray-300 rounded w-6"></div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
                <p className="text-red-600 mb-2">Erreur: {error}</p>
                <button 
                    onClick={fetchStats}
                    className="px-3 py-1 bg-primary-600 text-white rounded text-sm hover:bg-primary-700"
                >
                    Réessayer
                </button>
            </div>
        );
    }

    if (!stats) {
        return (
            <div className="bg-white border border-gray-200 rounded-lg p-6 text-center text-gray-500">
                <ChartBarIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                <p>Aucune donnée statistique disponible</p>
            </div>
        );
    }

    const percentages = calculatePercentages();
    const trendInfo = getTrendInfo();

    return (
        <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
            {/* En-tête avec note moyenne */}
            <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                    {renderStarsWithRating(parseFloat(stats.average_rating) || 0, "h-6 w-6")}
                </div>
                <p className="text-2xl font-bold text-gray-900">
                    {parseFloat(stats.average_rating).toFixed(1)} / 5
                </p>
                <p className="text-sm text-gray-600">
                    Basé sur {stats.total_reviews} avis
                </p>
                
                {/* Indicateur de tendance */}
                {trendInfo && (
                    <div className={`inline-flex items-center mt-2 px-3 py-1 rounded-full text-xs font-medium ${
                        trendInfo.trend === 'positive' 
                            ? 'bg-green-100 text-green-800'
                            : trendInfo.trend === 'negative'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                    }`}>
                        {trendInfo.trend === 'positive' ? (
                            <ArrowTrendingUpIcon className="h-3 w-3 mr-1" />
                        ) : trendInfo.trend === 'negative' ? (
                            <ArrowTrendingDownIcon className="h-3 w-3 mr-1" />
                        ) : (
                            <ChartBarIcon className="h-3 w-3 mr-1" />
                        )}
                        {trendInfo.message}
                    </div>
                )}
            </div>

            {/* Répartition détaillée */}
            <div className="space-y-3">
                <h4 className="font-medium text-gray-900 text-sm mb-3">Répartition des notes</h4>
                
                {[5, 4, 3, 2, 1].map((rating) => {
                    const count = stats[`rating_${rating}`] || 0;
                    const percentage = percentages[`rating_${rating}`] || 0;
                    
                    return (
                        <div key={rating} className="flex items-center space-x-3">
                            {/* Label étoiles */}
                            <div className="flex items-center min-w-0 flex-shrink-0">
                                <span className="text-sm font-medium text-gray-700 mr-1">
                                    {rating}
                                </span>
                                <StarIconSolid className="h-4 w-4 text-yellow-400" />
                            </div>
                            
                            {/* Barre de progression */}
                            <div className="flex-1 bg-gray-200 rounded-full h-2 min-w-0">
                                <div 
                                    className={`h-2 rounded-full transition-all duration-300 ${
                                        rating >= 4 ? 'bg-green-500' : 
                                        rating === 3 ? 'bg-yellow-500' : 'bg-red-500'
                                    }`}
                                    style={{ width: `${percentage}%` }}
                                />
                            </div>
                            
                            {/* Compteur et pourcentage */}
                            <div className="flex items-center space-x-2 min-w-0 flex-shrink-0">
                                <span className="text-sm text-gray-600">
                                    {count}
                                </span>
                                <span className="text-xs text-gray-500 w-8 text-right">
                                    {percentage}%
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Statistiques additionnelles */}
            <div className="border-t pt-4">
                <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                        <p className="text-lg font-semibold text-green-600">
                            {Math.round(((stats.rating_4 + stats.rating_5) / Math.max(stats.total_reviews, 1)) * 100)}%
                        </p>
                        <p className="text-xs text-gray-600">Avis positifs</p>
                    </div>
                    <div>
                        <p className="text-lg font-semibold text-blue-600">
                            {stats.total_reviews}
                        </p>
                        <p className="text-xs text-gray-600">Total avis</p>
                    </div>
                </div>
            </div>

            {/* Bouton pour actualiser */}
            <div className="border-t pt-4">
                <button
                    onClick={fetchStats}
                    className="w-full text-center text-sm text-gray-500 hover:text-primary-600 transition-colors"
                >
                    Actualiser les statistiques
                </button>
            </div>
        </div>
    );
};

export default ReviewStats;